// Panel: a section one tone above what it sits on, with an optional heading and an actions slot
// beside it. A panel keyed to a colour wears a bar beside its title, and lit, the module frame:
// a strip of that colour along its top edge and a soft glow. Stat, StatTile and Readout are the
// panel family's figures: one number at a glance, a tile that holds one, a recessed window that
// holds several. docs/operations/design-standard.md, "Surfaces" and "Panels and figures".

import React, { useState } from 'react';
import { CATEGORY, HUES, alpha, type CategoryHue } from './categoryColors';
import { IconChevronDown } from './icons';
import Reveal from './Reveal';
import { CONTROL_MOTION, FOCUS, STATE, useTicker } from './motion';

let seq = 0;

/** What the panel sits on: the page, or another panel (a card inside a section). It steps one up. */
export type PanelOn = 'ground' | 'panel';
/** `none` lays a table or list edge to edge, with the heading and footer still inset. */
export type PanelPadding = 'none' | 'sm' | 'md';
/** `section` sets the title as the section label: small, uppercase, tracked. */
export type PanelTitleStyle = 'heading' | 'section';
/** The colour a panel is keyed to: the instance accent, or a category hue. */
export type PanelAccent = 'primary' | CategoryHue;

const FILL: Record<PanelOn, string> = {
  ground: 'bg-surface-container-low',
  panel: 'bg-surface-container',
};

const PAD: Record<PanelPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
};

const TITLE: Record<PanelTitleStyle, string> = {
  heading: 'text-base font-semibold leading-6 text-on-surface',
  section: 'text-[11px] font-semibold uppercase tracking-widest leading-4 text-on-surface-variant',
};

const HEAD: Record<PanelTitleStyle, Record<PanelPadding, string>> = {
  heading: { none: 'px-4 pb-3 pt-4', sm: 'mb-2', md: 'mb-3' },
  section: { none: 'px-4 pb-2 pt-3', sm: 'mb-1.5', md: 'mb-2' },
};

// A collapsible header is one key across the row. With no inset padding the key takes the row's
// padding itself, symmetric so the closed panel is balanced; otherwise the gap below the heading
// moves into the body, so it closes with it.
const INSET_KEY = '-mx-2 -my-1 rounded-control px-2 py-1';
const COLLAPSE_KEY: Record<PanelTitleStyle, Record<PanelPadding, string>> = {
  heading: { none: 'px-4 py-3.5', sm: INSET_KEY, md: INSET_KEY },
  section: { none: 'px-4 py-3', sm: INSET_KEY, md: INSET_KEY },
};

const COLLAPSE_BODY: Record<PanelTitleStyle, Record<PanelPadding, string>> = {
  heading: { none: '', sm: 'pt-2', md: 'pt-3' },
  section: { none: '', sm: 'pt-1.5', md: 'pt-2' },
};

/** The accent at `a` strength, as CSS. Primary follows the instance; a hue takes its ink or mark. */
const accentAt = (accent: PanelAccent, role: 'ink' | 'mark', a: number): string =>
  accent === 'primary'
    ? `color-mix(in srgb, var(--color-primary, #ffcc33) ${Math.round(a * 100)}%, transparent)`
    : alpha(CATEGORY[accent][role], a);

const AccentBar: React.FC<{ accent: PanelAccent }> = ({ accent }) => (
  <span
    aria-hidden="true"
    data-accent={accent}
    className="h-3.5 w-1 flex-none rounded-full"
    style={{ backgroundColor: accentAt(accent, 'ink', 0.6) }}
  />
);

// The strip is the panel's own background image, so its radius clips it: no overflow-hidden to cut
// an open list or a ring, and no extra child for a caller's space-y to push the header down from.
const frameStyle = (accent: PanelAccent): React.CSSProperties => ({
  boxShadow: `0 0 40px -8px ${accentAt(accent, 'mark', 0.08)}`,
  backgroundImage: `linear-gradient(90deg, ${accentAt(accent, 'mark', 0.4)} 0%, ${accentAt(accent, 'mark', 0.1)} 50%, transparent 100%)`,
  backgroundSize: '100% 2px',
  backgroundRepeat: 'no-repeat',
});

export interface PanelCollapse {
  open: boolean;
  onToggle: () => void;
}

export interface PanelProps {
  title?: React.ReactNode;
  /** The whole header row shows and hides the body (and the footer). Needs a `title`. */
  collapse?: PanelCollapse;
  /** Controls that act on the whole panel, at the heading's right. */
  actions?: React.ReactNode;
  /** Status and actions for the whole panel, pinned to its bottom edge when the panel is
   *  stretched beside a taller neighbour, so the pair ends on one line. */
  footer?: React.ReactNode;
  /** Heading level, so the panel fits the page outline it sits in. */
  level?: 2 | 3;
  /** The accessible name of a panel with no visible title. */
  label?: string;
  /** `section` for dashboard widgets and record sections: the title as a small uppercase label. */
  titleStyle?: PanelTitleStyle;
  /** The colour the panel is keyed to, shown as a bar beside the title. Only where the colour means something. */
  accent?: PanelAccent;
  /** With `accent`, the module frame: a strip of the accent along the top edge and a soft glow. Keep it rare. */
  lit?: boolean;
  on?: PanelOn;
  padding?: PanelPadding;
  children: React.ReactNode;
  className?: string;
  id?: string;
}

export const Panel: React.FC<PanelProps> = ({
  title, collapse, actions, footer, level = 2, label, titleStyle = 'heading', accent, lit = false, on = 'ground',
  padding = 'md', children, className = '', id,
}) => {
  const [headingId] = useState(() => `ui-panel-${++seq}`);
  const Heading = level === 2 ? 'h2' : 'h3';
  const folding = collapse && title ? collapse : null;
  const open = folding ? folding.open : true;
  const frame = accent && lit ? accent : null;
  const bar = accent ? <AccentBar accent={accent} /> : null;
  return (
    <section
      id={id}
      aria-labelledby={title ? headingId : undefined}
      aria-label={title ? undefined : label}
      data-lit={frame ?? undefined}
      className={`flex min-w-0 flex-col rounded-panel ${FILL[on]} ${PAD[padding]} ${className}`}
      style={frame ? frameStyle(frame) : undefined}
    >
      {folding && (
        <div className="flex min-w-0 items-center gap-3">
          {/* The heading holds the key (the disclosure pattern), so the outline still finds it. */}
          <Heading id={headingId} className={`m-0 min-w-0 flex-1 ${TITLE[titleStyle]}`}>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={open ? `${headingId}-body` : undefined}
              onClick={folding.onToggle}
              className={`flex w-full min-w-0 items-center justify-between gap-3 text-left ${COLLAPSE_KEY[titleStyle][padding]} ${
                padding === 'none' ? (open ? 'rounded-t-panel' : 'rounded-panel') : ''
              } ${STATE} ${CONTROL_MOTION} ${FOCUS}`}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                {bar}
                <span className="min-w-0 text-balance">{title}</span>
              </span>
              <span aria-hidden="true" className="flex flex-none items-center gap-1 text-[13px] font-medium leading-[18px] text-on-surface-variant">
                {open ? 'Hide' : 'Show'}
                <IconChevronDown className={`h-4 w-4 transition-transform duration-standard ease-emphasized motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
              </span>
            </button>
          </Heading>
          {actions && <div className={`flex flex-none items-center gap-2 ${padding === 'none' ? 'pr-4' : ''}`}>{actions}</div>}
        </div>
      )}
      {!folding && (title || actions) && (
        <div className={`flex min-w-0 items-center justify-between gap-3 ${HEAD[titleStyle][padding]}`}>
          {title ? (
            <div className="flex min-w-0 items-center gap-2.5">
              {bar}
              <Heading id={headingId} className={`m-0 min-w-0 text-balance ${TITLE[titleStyle]}`}>
                {title}
              </Heading>
            </div>
          ) : <span />}
          {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
        </div>
      )}
      {folding ? (
        <Reveal show={open}>
          <div id={`${headingId}-body`} className={COLLAPSE_BODY[titleStyle][padding]}>{children}</div>
        </Reveal>
      ) : children}
      {/* A footer that comes and goes (a state with no actions) grows in and dissolves out. */}
      <div className="mt-auto">
        <Reveal show={!!footer && open}>
          <div className={padding === 'none' ? 'px-4 pb-4 pt-3' : 'pt-3'}>{footer}</div>
        </Reveal>
      </div>
    </section>
  );
};

// ── Figures ─────────────────────────────────────────────────────────────────

type MeaningTone = 'neutral' | 'accent' | 'good' | 'warn' | 'danger';
/** The figure's ink: a shared meaning, or a category hue (`blue`, `teal`) in that category's ink.
 *  Colour says the same thing as the label beside it, never something else. */
export type StatTone = MeaningTone | CategoryHue;

const INK: Record<MeaningTone, string> = {
  neutral: 'text-on-surface',
  accent: 'text-primary',
  good: 'text-green-300',
  warn: 'text-amber-300',
  danger: 'text-red-300',
};

const isHue = (tone: StatTone): tone is CategoryHue => tone in HUES;

/** `lg` is the hero figure a dashboard widget leads with. */
export type StatSize = 'sm' | 'md' | 'lg';

const FIGURE: Record<StatSize, string> = {
  sm: 'text-base font-semibold leading-6',
  md: 'text-2xl font-semibold leading-8',
  lg: 'text-3xl font-bold leading-9 tracking-tight',
};

export interface StatProps {
  /** What the figure counts, in words: "Ready to fly", "Uncategorized". */
  label: React.ReactNode;
  /** A number ticks to each new value; anything else (a formatted sum, a date) shows as given. */
  value: number | React.ReactNode;
  /** How a number is written while it ticks and at rest. */
  format?: (n: number) => string;
  /** One quieter line under the figure: the period, the comparison, the unit. */
  sub?: React.ReactNode;
  tone?: StatTone;
  size?: StatSize;
}

const Ticking: React.FC<{ value: number; format?: (n: number) => string }> = ({ value, format }) => {
  const shown = useTicker(value);
  return <>{format ? format(shown) : String(shown)}</>;
};

/** A label and its figure. Spans throughout, so it can sit inside a button. */
export const Stat: React.FC<StatProps> = ({ label, value, format, sub, tone = 'neutral', size = 'md' }) => (
  <span className="block min-w-0">
    <span className="block truncate text-[13px] font-medium leading-[18px] text-on-surface-variant">{label}</span>
    <span
      data-tone={tone}
      className={`mt-1 block truncate tabular-nums transition-colors duration-standard ease-emphasized ${FIGURE[size]} ${
        isHue(tone) ? '' : INK[tone]
      }`}
      style={isHue(tone) ? { color: CATEGORY[tone].ink.oklch } : undefined}
    >
      {typeof value === 'number' ? <Ticking value={value} format={format} /> : value}
    </span>
    {sub && <span className="mt-0.5 block truncate text-xs leading-4 text-on-surface-variant">{sub}</span>}
  </span>
);

export interface StatTileProps extends StatProps {
  on?: PanelOn;
  /** A small drawing at the tile's corner, from ui/icons. */
  icon?: React.ReactNode;
  /** Makes the tile a key: it shows or filters to what it counts. Wrap it in a Tooltip. */
  onClick?: () => void;
  /** A tile that toggles a view on and off; drawn lit while on. */
  pressed?: boolean;
  /** The module frame in the tile's own hue: a strip along the top edge and a soft glow. Needs a hue or `accent` tone. */
  lit?: boolean;
  className?: string;
}

/** One figure at a glance, on its own tile. */
export const StatTile: React.FC<StatTileProps> = ({ on = 'ground', icon, onClick, pressed, lit = false, className = '', ...stat }) => {
  const tone = stat.tone ?? 'neutral';
  const frame: PanelAccent | null = !lit ? null : isHue(tone) ? tone : tone === 'accent' ? 'primary' : null;
  // An inline glow would beat the pressed key's shadow-glow class, so a pressed tile keeps the strip only.
  const frameProps = {
    'data-lit': frame ?? undefined,
    style: frame ? { ...frameStyle(frame), ...(pressed ? { boxShadow: undefined } : {}) } : undefined,
  };
  const body = (
    <>
      <Stat {...stat} />
      {icon && <span aria-hidden="true" className="flex flex-none text-on-surface-variant">{icon}</span>}
    </>
  );
  const shape = `flex min-w-0 items-start justify-between gap-3 rounded-panel p-4 text-left ${FILL[on]}`;
  if (!onClick) return <div {...frameProps} className={`${shape} ${className}`}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      {...frameProps}
      className={`${shape} ${STATE} ${CONTROL_MOTION} ${FOCUS} ${pressed ? 'shadow-glow' : 'shadow-none'} ${className}`}
    >
      {body}
    </button>
  );
};

/** Several figures read together, in a window recessed into the panel that holds it. */
export const Readout: React.FC<{ label?: string; children: React.ReactNode; className?: string }> = ({
  label, children, className = '',
}) => (
  <div
    role="group"
    aria-label={label}
    className={`flex min-w-0 flex-wrap gap-x-8 gap-y-3 rounded-control bg-surface-container-high px-4 py-3 shadow-recess ${className}`}
  >
    {children}
  </div>
);

export default Panel;
